import { Alert, App as AntApp, Button, Card, Form, Popconfirm, Select, Space, Table, Typography, Upload } from "antd";
import { DeleteOutlined, DownloadOutlined, PaperClipOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { DOCUMENT_ACCEPT, MAX_UPLOAD_BYTES, MAX_UPLOAD_MB, looksAcceptable, multipart, saveBlob } from "../app/files.ts";
import { useReportError } from "../app/students.ts";

type DocumentKind = components["schemas"]["StudentDocumentKind"];
type StudentDocument = components["schemas"]["StudentDocumentDetails"];
type UploadForm = components["schemas"]["UploadStudentDocumentForm"];

const kinds: DocumentKind[] = ["BirthCertificate", "Passport", "Contract", "MedicalCertificate", "Other"];
const fields = ["kind"] as const;
const KB = 1024;

interface DocumentForm {
  kind?: DocumentKind;
}

export function StudentDocumentsTab({ studentId, canManage }: { studentId: string; canManage: boolean }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<DocumentForm>();
  const reportError = useReportError(form, fields);
  const reportPlain = useReportError();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const queryKey = ["students", studentId, "documents"];

  const documents = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/students/{id}/documents", { params: { path: { id: studentId } } });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
  });

  const upload = useMutation({
    mutationFn: async ({ kind, chosen }: { kind: DocumentKind; chosen: File }) => {
      const { data, error, response } = await api.POST("/students/{id}/documents", {
        params: { path: { id: studentId } },
        body: multipart<UploadForm>({ kind, file: chosen }),
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      form.resetFields();
      setFile(null);
      setFileError(null);
      void message.success(t("student.documents.uploaded"));
    },
    onError: (err: unknown) => {
      // The file is not a form field (it lives in state), so its server error is shown under the picker.
      const fileErrors = err instanceof ApiError ? err.fieldErrors()["file"] : undefined;
      if (fileErrors) setFileError(fileErrors.join(" "));
      else reportError(err);
    },
  });

  const download = useMutation({
    mutationFn: async (document: StudentDocument) => {
      const { data, error, response } = await api.GET("/students/{id}/documents/{documentId}", {
        params: { path: { id: studentId, documentId: document.id } },
        parseAs: "blob",
      });
      if (!data) throw new ApiError(response.status, error);
      saveBlob(data, document.fileName);
    },
    onError: reportPlain,
  });

  const remove = useMutation({
    mutationFn: async (documentId: string) => {
      const { error, response } = await api.DELETE("/students/{id}/documents/{documentId}", { params: { path: { id: studentId, documentId } } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      void message.success(t("student.documents.deleted"));
    },
    onError: reportPlain,
  });

  function choose(chosen: File) {
    const problem = !looksAcceptable(chosen, false)
      ? t("student.documents.wrongType")
      : chosen.size > MAX_UPLOAD_BYTES
        ? t("student.documents.tooLarge", { size: MAX_UPLOAD_MB })
        : null;
    setFile(problem ? null : chosen);
    setFileError(problem);
    return Upload.LIST_IGNORE;
  }

  const size = (bytes: number) =>
    bytes < KB * KB ? t("student.documents.sizeKb", { size: Math.max(1, Math.round(bytes / KB)) }) : t("student.documents.sizeMb", { size: (bytes / KB / KB).toFixed(1) });
  const required = { required: true, message: t("family.validation.required") };

  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      {canManage && (
        <Card size="small" title={t("student.documents.upload")} style={{ maxWidth: 640 }}>
          <Form<DocumentForm>
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={(values) => {
              if (!file) setFileError(t("family.validation.required"));
              else if (values.kind) upload.mutate({ kind: values.kind, chosen: file });
            }}
          >
            <Form.Item name="kind" label={t("student.documents.kind")} rules={[required]}>
              <Select options={kinds.map((k) => ({ value: k, label: t(`student.documentKinds.${k}`) }))} />
            </Form.Item>
            <Form.Item
              label={t("student.documents.file")}
              extra={t("student.documents.hint", { size: MAX_UPLOAD_MB })}
              {...(fileError ? { validateStatus: "error" as const, help: fileError } : {})}
            >
              <Space wrap>
                <Upload accept={DOCUMENT_ACCEPT} showUploadList={false} beforeUpload={choose}>
                  <Button icon={<PaperClipOutlined />}>{t("student.documents.chooseFile")}</Button>
                </Upload>
                {file && <Typography.Text>{file.name}</Typography.Text>}
              </Space>
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={upload.isPending}>
              {t("student.documents.submit")}
            </Button>
          </Form>
        </Card>
      )}

      {documents.isError ? (
        <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void documents.refetch()}>{t("app.retry")}</Button>} />
      ) : (
        <Table<StudentDocument>
          rowKey="id"
          size="small"
          loading={documents.isPending}
          dataSource={documents.data ?? []}
          pagination={false}
          scroll={{ x: true }}
          locale={{ emptyText: t("student.documents.empty") }}
          columns={[
            { key: "kind", title: t("student.documents.kind"), render: (_, d) => t(`student.documentKinds.${d.kind}`) },
            { key: "fileName", title: t("student.documents.fileName"), render: (_, d) => <Typography.Text ellipsis={{ tooltip: d.fileName }} style={{ maxWidth: 240 }}>{d.fileName}</Typography.Text> },
            { key: "size", title: t("student.documents.size"), render: (_, d) => size(Number(d.sizeBytes)) },
            { key: "uploadedAt", title: t("student.documents.uploadedAt"), render: (_, d) => dayjs(d.uploadedAt).format("DD.MM.YYYY HH:mm") },
            {
              key: "actions",
              title: t("student.documents.actions"),
              render: (_, d) => (
                <Space>
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    loading={download.isPending && download.variables.id === d.id}
                    onClick={() => download.mutate(d)}
                  >
                    {t("student.documents.download")}
                  </Button>
                  {canManage && (
                    <Popconfirm
                      title={t("student.documents.deleteConfirm", { name: d.fileName })}
                      okText={t("student.documents.delete")}
                      cancelText={t("family.cancel")}
                      okButtonProps={{ danger: true }}
                      onConfirm={() => remove.mutate(d.id)}
                    >
                      <Button type="link" danger icon={<DeleteOutlined />} loading={remove.isPending && remove.variables === d.id}>
                        {t("student.documents.delete")}
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]}
        />
      )}
    </Space>
  );
}
